import { default as Modal } from 'antd/es/modal';

import type { AuthScheme, Webhook } from 'api';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';
import { SecondaryButton } from '@atoms/buttons/SecondaryButton';

import {
  AFFECTED_WEBHOOKS_UNKNOWN,
  NO_BACKFILL_WARNING,
  TOGGLE_MODAL_WIDTH,
  TOGGLE_SCHEME_AFFECTED_ROW_TEST_ID,
  TOGGLE_SCHEME_CANCEL_TEST_ID,
  TOGGLE_SCHEME_CONFIRM_TEST_ID,
  TOGGLE_SCHEME_IN_USE_ERROR_TEST_ID,
  TOGGLE_SCHEME_MODAL_TEST_ID
} from './constants';
import { useAffectedWebhooks } from './useAffectedWebhooks';
import { useToggleSchemeStatus } from './useToggleSchemeStatus';
import {
  affectedWebhooksIntro,
  toggleSchemeBody,
  toggleSchemeConfirmLabel,
  toggleSchemeTitle
} from './utils';

import { webhookLabel } from '../../utils/webhookLabel';

type ToggleSchemeStatusModalProps = {
  authenticationKey: AuthScheme;
  open: boolean;
  onCancel: () => void;
  onConfirmed: () => void;
};

type BodyProps = {
  disabling: boolean;
  affected: Webhook[];
  lookupFailed: boolean;
};

const Body = ({ disabling, affected, lookupFailed }: BodyProps) => {
  if (!disabling || (affected.length === 0 && !lookupFailed)) {
    return <p>{toggleSchemeBody(disabling)}</p>;
  }
  if (lookupFailed) {
    return (
      <>
        <p>{toggleSchemeBody(true)}</p>
        <p>{AFFECTED_WEBHOOKS_UNKNOWN}</p>
      </>
    );
  }
  return (
    <>
      <p>{affectedWebhooksIntro(affected.length)}</p>
      <ul className="flex list-disc flex-col gap-1 pl-5">
        {affected.map((webhook) => (
          <li
            key={webhook.uuid}
            data-testid={TOGGLE_SCHEME_AFFECTED_ROW_TEST_ID(webhook.uuid)}
            className="break-all"
          >
            {webhookLabel(webhook)}
          </li>
        ))}
      </ul>
      <p>{NO_BACKFILL_WARNING}</p>
    </>
  );
};

export const ToggleSchemeStatusModal = ({
  authenticationKey,
  open,
  onCancel,
  onConfirmed
}: ToggleSchemeStatusModalProps) => {
  const { toggle, isPending, blockedError, clearBlockedError } =
    useToggleSchemeStatus();
  const { affected, isLoading, isError } =
    useAffectedWebhooks(authenticationKey);
  const disabling = authenticationKey.enabled;
  const handleCancel = () => {
    clearBlockedError();
    onCancel();
  };

  return (
    <Modal
      open={open}
      onCancel={handleCancel}
      width={TOGGLE_MODAL_WIDTH}
      destroyOnHidden
      title={
        <div className="text-lg font-medium text-content-primary">
          {toggleSchemeTitle(authenticationKey.enabled)}
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <SecondaryButton
            data-testid={TOGGLE_SCHEME_CANCEL_TEST_ID}
            onClick={handleCancel}
            disabled={isPending}
          >
            Cancel
          </SecondaryButton>
          <PrimaryButton
            data-testid={TOGGLE_SCHEME_CONFIRM_TEST_ID}
            danger={authenticationKey.enabled}
            type={authenticationKey.enabled ? 'default' : 'primary'}
            loading={isPending}
            disabled={disabling && isLoading}
            onClick={() => toggle(authenticationKey, onConfirmed)}
          >
            {toggleSchemeConfirmLabel(authenticationKey.enabled)}
          </PrimaryButton>
        </div>
      }
    >
      <div
        data-testid={TOGGLE_SCHEME_MODAL_TEST_ID}
        className="flex flex-col gap-2 text-base text-content-primary"
      >
        <Body
          disabling={disabling}
          affected={affected}
          lookupFailed={isError}
        />
        {blockedError && (
          <p
            data-testid={TOGGLE_SCHEME_IN_USE_ERROR_TEST_ID}
            className="text-feedback-error"
          >
            {blockedError}
          </p>
        )}
      </div>
    </Modal>
  );
};
